# Salary

The SEEK API has added support for a monthly salary interval and additional currencies.

This does not change our guidance around [salary validation] nor our existing model for salary visibility.
The [salary range] is used to provide more relevant job search results to candidates,
but the monetary values in [`minimumAmount`] and [`maximumAmount`] are not visible in search results nor on the job ad.
The hirer can provide an optional [salary description] to describe the remuneration package,
and SEEK may display the [pay type](#pay-type) and [currency](#currency) alongside the description for clarity.

[`maximumamount`]: /schema/named-type/RemunerationRangeInput#/field/maximumAmount
[`minimumamount`]: /schema/named-type/RemunerationRangeInput#/field/minimumAmount
[salary description]: /use-cases/job-posting/salary#salary-description
[salary range]: /use-cases/job-posting/salary#salary-range
[salary validation]: /use-cases/job-posting/salary#validation

import {
  CommissionOnly,
  Monthly
} from '../../../use-cases/job-posting/assets/PayType';

## Pay type

The `CommissionOnly` value is now deprecated in [`RemunerationPackageInput.basisCode`].
You should remove the option from your software.

<CommissionOnly />

[`remunerationpackageinput.basiscode`]: /schema/named-type/RemunerationPackageInput#/field/basisCode

It is common to express salaries with a `Month`ly interval in certain markets.
This value is now supported in [`RemunerationRangeInput.intervalCode`] and complements the existing `Hour`ly and `Year`ly intervals.

<Monthly />

[`remunerationrangeinput.intervalcode`]: /schema/named-type/RemunerationRangeInput#/field/intervalCode

We recommend using the pay type labels documented above for consistency with SEEK.
(The “Remove” and “New” badges displayed above only serve to clarify our documentation.
Your software should not display these badges on its job posting form.)

The SEEK API now supports dynamically querying valid pay type combinations with the [`payTypes` query].
These may be presented to a hirer for selection in a job posting flow.

This can help prevent entering a nonsensical pay type combination.
For example, a job ad with an hourly rate of $70,000 will render a hirer’s job ad undiscoverable in recommendations and search

```graphql
query ($schemeId: String!) {
  payTypes(schemeId: $schemeId) {
    basisCode
    intervalCode
    label
  }
}
```

```json label="Variables"
{
  "schemeId": "seekAnzPublicTest"
}
```

```json label="Result"
{
  "payTypes": [
    {
      "basisCode": "Hourly",
      "intervalCode": "Hour",
      "label": "Hourly rate"
    },
    {
      "basisCode": "Salaried",
      "intervalCode": "Month",
      "label": "Monthly salary"
    },
    {
      "basisCode": "Salaried",
      "intervalCode": "Year",
      "label": "Annual salary"
    },
    {
      "basisCode": "SalariedPlusCommission",
      "intervalCode": "Year",
      "label": "Annual plus commission"
    }
  ]
}
```

[`paytypes` query]: /schema/query/payTypes

## Currency

Previously, the SEEK API specified location-bound rules that covered 3 salary currencies:

> ... the [position’s location] determines the required currency:
>
> - `NZD` is required for positions located in New Zealand.
>   These are locations that have a [`countryCode`] of `NZ`.
> - `GBP` is required for positions located in the UK & Ireland.
>   These are locations that have a [`countryCode`] of `GB` or `IE`.
>
>   Note that this requires positions in the Republic of Ireland to use salaries denominated in `GBP`, even though their local currency is `EUR`.
>
> - `AUD` is required for positions in all other locations, regardless of their local currency.
>
> While the currency can be derived from the location, it must be explicitly specified to ensure the appropriate currency was presented to the hirer in the job posting flow.
> An explicit currency will also allow the SEEK API to accept additional currencies in a backward compatible way.

The SEEK API now supports 16 currencies in total.
To assist hirers in selecting the right currency for their job ad, we recommend:

1. Displaying a shortlist of the 8 local currencies used across our Asia-Pacific markets in addition to the United States Dollar (USD)
2. Pre-selecting the currency based on the `currencies` value returned for the hirer’s selected location

### 1. Display a shortlist of currencies

The shortlist of currencies can be dynamically queried with the [`currencies` query].
A query-based approach will allow your hirers to select new currencies if they are added in future.

```graphql
query ($usageTypeCode: String!) {
  currencies(usageTypeCode: $usageTypeCode) {
    code
  }
}
```

```json
{
  "usageTypeCode": "SEEKMarket"
}
```

```json
{
  "currencies": [
    {
      "code": "AUD"
    },
    {
      "code": "HKD"
    },
    {
      "code": "IDR"
    },
    {
      "code": "MYR"
    },
    {
      "code": "NZD"
    },
    {
      "code": "PHP"
    },
    {
      "code": "SGD"
    },
    {
      "code": "THB"
    },
    {
      "code": "USD"
    }
  ]
}
```

[`currencies` query]: /schema/query/currencies

Any supported currency can be used in any location.
SEEK will take care of currency conversions when candidates refine their job searches by [salary range].
SEEK will not perform conversions on the free-text [salary description];
we recommend hirers to provide a description in the dominant currency of the [position’s location] with the currency clearly denoted.

### 2. Pre-selecting currency based on location

Our [location implementation options] now provide the recommended `currencies` for a given location.
The [ISO 4217] currency codes are listed in order of preference.
In most cases there will be a single currency returned,
and we recommend pre-selecting the first option for an optimal hirer experience:

import { SalaryCurrency } from '../../../use-cases/job-posting/assets/SalaryCurrency';

<SalaryCurrency />

For currencies supported by the SEEK API,
your software must supply the same currency that the hirer sees when they are filling out the pay details of their position.
If your posting form includes currencies that are not supported by the SEEK API,
you may programmatically convert the salary range to `USD`.

### Behaviour change for mismatched inputs

Previously, the SEEK API would silently ignore mismatched currency inputs and replace them with the expected currency for the position location.
For example, if you sent `NZD` for a job ad located in Australia, we would replace the currency with `AUD`.
This behaviour was erroneous as it did not account for currency conversions,
and it has been removed now that the SEEK API accepts any supported currency in any location.
The currency that your software provides will be respected on the job ad,
and providing an unsupported currency will fail with a [`BAD_USER_INPUT`] error.

[`countrycode`]: /schema/named-type/Location#/field/countryCode
[iso 4217]: https://en.wikipedia.org/wiki/ISO_4217
[position’s location]: /use-cases/job-posting/locations

## Salary range

We have revised our UX recommendations for salary ranges.
These changes are focused on supporting Asia-Pacific markets and ensuring hirers get optimal performance from their SEEK job ads.

1. Display the salary currency alongside the range.

   This dropdown should be implemented as documented in the section above.

2. Mark the maximum amount as a mandatory input.

   We have observed that some hirers currently omit the maximum amount,
   which defaults it to the minimum and affects the performance of their job ads.
   The input field will be required in our schema in future.

3. Display the pay type alongside the range.

   We have observed that some hirers currently enter hourly salary ranges against an annual pay type and vice versa,
   rendering their job ads undiscoverable in recommendations and search results.

import { SalaryRange } from '../../../use-cases/job-posting/assets/SalaryRange';

<SalaryRange />

[`bad_user_input`]: /graphql/error-responses#bad_user_input
[location implementation options]: /use-cases/job-posting/locations#implementation-options
